import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AgencyDashboardService } from '@/services/agencyDashboard.service';
import {
  AgencyDashboardOverviewResult,
  AgencyDashboardPeriod,
} from '@/integrations/supabase/rpc-types';

interface UseAgencyDashboardStatsResult {
  data: AgencyDashboardOverviewResult | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Il periodo è controllato dal componente padre (condiviso tra le tab della dashboard
 * agenzia) e passato qui solo per guidare il refetch.
 */
export function useAgencyDashboardStats(
  period: AgencyDashboardPeriod
): UseAgencyDashboardStatsResult {
  const [data, setData] = useState<AgencyDashboardOverviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await AgencyDashboardService.getOverview(period);
      setData(result);
    } catch {
      const msg = String(t('errors.loadFailed'));
      setError(msg);
      toast({
        title: String(t('common.error')),
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
    // `t` da useI18n() non è memoizzato (nuova closure ad ogni render): non va in
    // dipendenza, altrimenti fetchData cambia identità ad ogni render e l'effect
    // sotto la richiama all'infinito (specialmente visibile quando la call fallisce).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}

export default useAgencyDashboardStats;
